import * as React from 'react';
import { Box, Typography, styled } from '@mui/material';
import moment, { Moment } from 'moment';
import { CalendarEventOutput } from '../calendar-types';
import { getEventColor } from './calendar-colors';
import { HOUR_HEIGHT, HOURS, getNowMinutes, getTzLabel } from './calendar-utils';
import { EventSegment, computeOverlapLayout, expandToSegments } from './overlap-layout';

interface WeekViewProps {
  weekStart: Moment;
  events: CalendarEventOutput[];
  onEventClick: (event: CalendarEventOutput) => void;
  onSlotClick: (datetime: string) => void;
}

const getSegmentPosition = (seg: EventSegment, weekStart: Moment) => {
  const dayIndex = seg.effectiveStart.diff(weekStart, 'days');
  if (dayIndex < 0 || dayIndex > 6) return null;
  const startMinutes = seg.effectiveStart.hours() * 60 + seg.effectiveStart.minutes();
  const durationMinutes = Math.max(seg.effectiveEnd.diff(seg.effectiveStart, 'minutes'), 30);
  return {
    dayIndex,
    top: (startMinutes / 60) * HOUR_HEIGHT,
    height: (durationMinutes / 60) * HOUR_HEIGHT,
  };
};


export const WeekView = ({ weekStart, events, onEventClick, onSlotClick }: WeekViewProps) => {
  const today = moment().startOf('day');
  const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days'));
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [nowMinutes, setNowMinutes] = React.useState(getNowMinutes);

  const isCurrentWeek = today.isSameOrAfter(weekStart, 'day') && today.isSameOrBefore(weekStart.clone().endOf('week'), 'day');

  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTo = isCurrentWeek ? (nowMinutes / 60) * HOUR_HEIGHT - 80 : HOUR_HEIGHT * 8;
      scrollRef.current.scrollTop = Math.max(0, scrollTo);
    }
  }, []);

  React.useEffect(() => {
    const timer = setInterval(() => setNowMinutes(getNowMinutes()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const nowTop = (nowMinutes / 60) * HOUR_HEIGHT;
  const todayIndex = isCurrentWeek ? today.diff(weekStart, 'days') : -1;

  const allSegments = React.useMemo(() => expandToSegments(events), [events]);
  const layoutsByDay = React.useMemo(
    () =>
      Array.from({ length: 7 }, (_, dayIndex) => {
        const daySegments = allSegments.filter(
          (s) => getSegmentPosition(s, weekStart)?.dayIndex === dayIndex
        );
        return computeOverlapLayout(daySegments);
      }),
    [allSegments, weekStart]
  );

  const handleSlotClick = (day: Moment, hour: number) => {
    onSlotClick(day.clone().hours(hour).minutes(0).seconds(0).toISOString());
  };

  return (
    <Root>
      <HeaderRow>
        <TzCorner>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {getTzLabel()}
          </Typography>
        </TzCorner>
        {days.map((day, i) => {
          const isToday = day.isSame(today, 'day');
          return (
            <DayHeaderCell key={i}>
              <Typography
                variant="caption"
                display="block"
                sx={{ fontSize: 11, color: isToday ? 'primary.main' : 'text.secondary', fontWeight: isToday ? 600 : 400, letterSpacing: 0.5 }}
              >
                {day.format('ddd').toUpperCase()}
              </Typography>
              <DateCircle isToday={isToday}>
                <Typography sx={{ color: isToday ? 'common.white' : 'text.primary', fontWeight: isToday ? 700 : 400, fontSize: 16, lineHeight: 1 }}>
                  {day.format('D')}
                </Typography>
              </DateCircle>
            </DayHeaderCell>
          );
        })}
      </HeaderRow>

      <ScrollArea ref={scrollRef}>
        <GridContainer>
          <TimeLabelsColumn>
            {HOURS.map((hour) => (
              <HourLabelCell key={hour}>
                {hour > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, userSelect: 'none' }}>
                    {moment().hours(hour).format('h a')}
                  </Typography>
                )}
              </HourLabelCell>
            ))}
          </TimeLabelsColumn>

          {days.map((day, dayIndex) => {
            const layouts = layoutsByDay[dayIndex];
            const isToday = dayIndex === todayIndex;
            return (
              <DayColumn key={dayIndex} isToday={isToday}>
                {HOURS.map((hour) => (
                  <SlotCell key={hour} onClick={() => handleSlotClick(day, hour)} />
                ))}

                {isToday && (
                  <NowLineContainer style={{ top: nowTop }}>
                    <NowLine>
                      <NowDot />
                    </NowLine>
                  </NowLineContainer>
                )}

                {layouts.map(({ segment, col, totalCols }) => {
                  const pos = getSegmentPosition(segment, weekStart);
                  if (!pos) return null;
                  const { event, isContinuation, continuesNextDay } = segment;
                  const style = getEventColor(event.colorId, event.status);
                  const isCancelled = event.status === 'cancelled';
                  const colWidth = 100 / totalCols;
                  return (
                    <EventBlock
                      key={`${event.id}-${segment.effectiveStart.valueOf()}`}
                      onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                      style={{
                        top: pos.top + 1,
                        height: Math.max(pos.height - 2, 22),
                        backgroundColor: style.bg,
                        borderLeft: `3px solid ${style.border}`,
                        left: `calc(${col * colWidth}% + 2px)`,
                        width: `calc(${colWidth}% - 4px)`,
                        borderTopLeftRadius: isContinuation ? 0 : 4,
                        borderTopRightRadius: isContinuation ? 0 : 4,
                        borderBottomLeftRadius: continuesNextDay ? 0 : 4,
                        borderBottomRightRadius: continuesNextDay ? 0 : 4,
                        borderBottom: continuesNextDay ? `2px dashed ${style.border}` : undefined,
                        borderTop: isContinuation ? `2px dashed ${style.border}` : undefined,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: style.text, fontWeight: 600, fontSize: 11, lineHeight: 1.2, textDecoration: isCancelled ? 'line-through' : 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.summary || '(No title)'}
                        {pos.height < 40 && !isContinuation && (
                          <Box component="span" sx={{ fontWeight: 400, opacity: 0.8 }}>
                            , {moment(event.startDateTime).format('h:mm a')}
                          </Box>
                        )}
                      </Typography>
                      {pos.height >= 40 && (
                        <Typography variant="caption" sx={{ color: style.text, fontSize: 10, opacity: 0.8 }}>
                          {isContinuation
                            ? `↵ until ${moment(event.endDateTime).format('h:mm a')}`
                            : `${moment(event.startDateTime).format('h:mm')}–${continuesNextDay ? `${moment(event.endDateTime).format('h:mm a')} (+1)` : moment(event.endDateTime).format('h:mm a')}`}
                        </Typography>
                      )}
                    </EventBlock>
                  );
                })}
              </DayColumn>
            );
          })}
        </GridContainer>
      </ScrollArea>
    </Root>
  );
};

interface WithIsToday {
  isToday?: boolean;
}

const Root = styled(Box)({
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  flexDirection: 'column',
});

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

const TzCorner = styled(Box)({
  width: 56,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  paddingBottom: 8,
});

const DayHeaderCell = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: 'center',
  paddingTop: 8,
  paddingBottom: 8,
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

const DateCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<WithIsToday>(({ theme, isToday }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  marginTop: 2,
  backgroundColor: isToday ? theme.palette.primary.main : 'transparent',
}));

const ScrollArea = styled(Box)({
  flex: 1,
  overflow: 'auto',
  position: 'relative',
});

const GridContainer = styled(Box)({
  display: 'flex',
  minHeight: HOUR_HEIGHT * 24,
});

const TimeLabelsColumn = styled(Box)({
  width: 56,
  flexShrink: 0,
  position: 'relative',
});

const HourLabelCell = styled(Box)({
  height: HOUR_HEIGHT,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  paddingRight: 8,
  paddingTop: 4,
});

const DayColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<WithIsToday>(({ theme, isToday }) => ({
  flex: 1,
  borderLeft: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  backgroundColor: isToday ? 'rgba(25,118,210,0.02)' : 'transparent',
}));

const SlotCell = styled(Box)(({ theme }) => ({
  height: HOUR_HEIGHT,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const NowLineContainer = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  zIndex: 3,
  pointerEvents: 'none',
});

const NowLine = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 2,
  backgroundColor: theme.palette.error.main,
}));

const NowDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: -5,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.error.main,
}));

const EventBlock = styled(Box)({
  position: 'absolute',
  borderRadius: 4,
  paddingLeft: 6,
  paddingRight: 6,
  paddingTop: 2,
  paddingBottom: 2,
  cursor: 'pointer',
  overflow: 'hidden',
  zIndex: 1,
  transition: 'filter 0.1s',
  '&:hover': { filter: 'brightness(0.95)', zIndex: 2 },
});
